import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { SECTION_GRID } from './sections/sectionTypes';

const DETAIL_TAB_COUNT = 6;

/** Mirrors the tab strip and the Basic Info field grid so the first paint does not jump. */
export function DetailSkeleton({ tabCount = DETAIL_TAB_COUNT }: { tabCount?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" className="rounded-xl border border-border bg-card shadow-sm">
      <span className="sr-only">{t('Loading...')}</span>
      <div className="flex gap-3 overflow-hidden border-b border-border px-4 py-3.5">
        {Array.from({ length: tabCount }, (_, i) => <Skeleton key={i} className="h-5 w-24 shrink-0" aria-hidden="true" />)}
      </div>
      <div className={`${SECTION_GRID} p-4 sm:p-6`}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`flex flex-col gap-2 ${i < 2 ? 'sm:col-span-2' : ''}`}>
            <Skeleton className="h-3 w-24" aria-hidden="true" />
            <Skeleton className={i === 1 ? 'h-20 w-full' : 'h-10 w-full'} aria-hidden="true" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Route fallback while the detail page's code downloads: the PageLayout header and the detail skeleton,
 * at the same sizes, so the page never goes blank and nothing shifts when it arrives.
 */
export function DetailRouteFallback() {
  return (
    <div>
      <div className="mx-4 mt-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm sm:mx-6" aria-hidden="true">
        <div className="h-[3px] bg-muted" />
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-6">
          <Skeleton className="h-14 w-14 shrink-0 rounded-xl" />
          <Skeleton className="h-7 w-56" />
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 py-8">
        <DetailSkeleton />
      </div>
    </div>
  );
}
